import { NavLink, Outlet } from 'react-router-dom';
import type { Role } from '../../../src/admin/types';
import { useAuth } from '../auth/AuthContext';
import { EnvBanner } from './EnvBanner';

interface NavItem {
  to: string;
  label: string;
  minRole: Role;
}

const NAV: readonly NavItem[] = [
  { to: '/', label: 'ダッシュボード', minRole: 'viewer' },
  { to: '/line-users', label: 'LINE ユーザー', minRole: 'viewer' },
  // Phase 2 placeholders (flow / prompts) hidden from nav until they have real content.
  { to: '/audit', label: '監査ログ', minRole: 'editor' },
  { to: '/users', label: 'ユーザー管理', minRole: 'admin' },
];

const ROLE_RANK: Record<Role, number> = { viewer: 1, editor: 2, admin: 3 };

function canSee(itemMin: Role, userRole: Role | null): boolean {
  if (!userRole) return false;
  return ROLE_RANK[userRole] >= ROLE_RANK[itemMin];
}

/**
 * Authenticated shell — env banner across the top, sidebar nav on the left,
 * outlet renders the active page. Routed under `ProtectedRoute`.
 */
export function Shell(): JSX.Element {
  const { user, logout } = useAuth();
  const role = user?.role ?? null;
  return (
    <div className="flex min-h-screen flex-col">
      <EnvBanner />
      <div className="flex flex-1">
        <aside className="w-56 border-r border-gray-200 bg-gray-50 p-4">
          <div className="mb-4 text-sm font-semibold text-gray-700">dss2-line admin</div>
          <nav className="flex flex-col gap-1">
            {NAV.filter((n) => canSee(n.minRole, role)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `rounded px-2 py-1 text-sm ${
                    isActive ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 border-t border-gray-200 pt-4 text-xs text-gray-600">
            {user ? (
              <>
                <div className="truncate" title={user.email}>
                  {user.email}
                </div>
                <div className="mt-1 text-gray-500">権限: {user.role ?? '未設定'}</div>
                <button
                  type="button"
                  onClick={logout}
                  className="mt-3 rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                >
                  ログアウト
                </button>
              </>
            ) : null}
          </div>
        </aside>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
